import 'babel-polyfill';
import React from 'react';
import { hydrate } from 'react-dom';
import { match } from 'react-router';
import { get } from 'lodash';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import { handleLeave } from 'utils/routes/handleLeave';

import config from 'config';
import Root, { routes } from './Root';
import { store, history, network, dataSync } from './configureStore';
import { addOnEnterHandlers } from 'utils/onEnterHandler';

import authorisationProblemRedirects from 'apps/user/models/authorisationProblemRedirects';

import 'themes/[theme]/styles/style.scss';

/* eslint-disable no-console */
const processedRoutes = addOnEnterHandlers(routes, /* onEnterExtras= */{
  // NOTE: make sure extras object stays consistent with the one passed in server/middleware/renderRavenApp.js
  store,
  routes,
  network,
  history,
});

const markRouteAsRaven = () => {
  // in order to have the 'back' button working on the proxied webshop page, we must somehow mark the routes handled
  // by Raven, so then webshop knows whether it should reload the page on browser history pop event, for more details
  // please look at /webshop-core/src/main/webapp/scripts/ravenIntegration/raven.history.js
  if (!window.history.state || !window.history.state.raven) {
    window.history.replaceState(
      { ...window.history.state, raven: true },
      '',
      window.location.toString(),
    );
  }
};

if (config.sentryDSNPublic) {
  Sentry.init({
    dsn: config.sentryDSNPublic,
    release: config.revision,
    allowUrls: config.sentryWhitelistUrls,
    integrations: [
      Integrations.dedupeIntegration(),
      Sentry.httpContextIntegration(),
      Integrations.rewriteFramesIntegration(),
    ],
    defaultIntegrations: false,
    sampleRate: 0.8, // Configures the sample rate for error events,80% of error events will be sent
    sendClientReports: false,
  });

  Sentry.setTag('ClientType', 'Browser');
  Sentry.setTag('ApplicationName', 'Raven');
}
const prevLocation = {
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
}; // initial value for prevLocation

// eslint-disable-next-line consistent-return
history.listenBefore((location, callback) => {
  const nextUrl = `${location.pathname}${location.search}`;
  const shouldReload = handleLeave({ location: prevLocation }, nextUrl);
  if (shouldReload) {
    return callback(false); // Stop navigation if a reload is required
  }
  // Update prevLocation to track the current route as the "previous" route
  // for the next navigation event. This ensures we always compare the last
  // visited route with the next one.
  prevLocation.pathname = location.pathname;
  prevLocation.search = location.search;
  prevLocation.hash = location.hash;

  // Manually run match to force onEnter hooks trigger
  match({ routes: processedRoutes, location }, (err, redirectLocation, renderProps) => {
    const { user } = store.getState();

    const nonRavenRoute = !err && !redirectLocation && !renderProps;
    const { noRouteAccess } = user;

    if (nonRavenRoute || noRouteAccess) {
      if (noRouteAccess !== 'DO_NOT_REDIRECT') {
        window.location.href = nextUrl;
      }
      markRouteAsRaven();
      return callback(false);
    }

    // Check for authorisationProblem and prevent navigation and redirect if necessary
    const violation = get(user, 'accountDetails.authorisationProblem');

    if (violation) {
      /**
       * We will land in this part of code once or twice. This is because violationRedirectUrl may be a Raven route.
       * If violationRedirectUrl is a non Raven route, it will be caught and navigated to with window.location.href
       *   using code above.
       * If violationRedirectUrl is a Raven route, we will land in this if() for the second time.
       */
      const violationRedirectUrl = authorisationProblemRedirects[violation];
      if (nextUrl !== violationRedirectUrl) {
        // This line will cause history.listenBefore to run again.
        // If violationRedirectUrl is null, will redirect to '/'.
        history.push(violationRedirectUrl);
      }

      return callback(true);
    }

    // Proceed with navigation
    return callback(true);
  });
});

history.listen(location => {
  match({ routes, location }, (err, redirectLocation, renderProps) => {
    if (!err && !redirectLocation && !renderProps) {
      window.location.reload();
      return;
    }

    if (renderProps) {
      if (network.poolChangeRequest) {
        network.setPoolSwitchCookie();
        window.location.reload();
      }

      // dispatch background data sync actions
      try {
        dataSync.trigger();
      } catch (error) {
        console.error(error);
      }
    }
  });
});

// make sure all of the async behavior has been resolved before the initial render,
// to avoid a mismatch between the server rendered and client rendered markup;
// do not check err and redirectLocation as the content has already been rendered on the server
match({ history, routes: processedRoutes }, (error, redirectLocation, renderProps) => {
  window.appContainer = document.getElementById('react-view');

  hydrate(
    <Root
      store={store}
      renderProps={renderProps}
    />,
    window.appContainer,
  );
});

if (module.hot) {
  module.hot.accept();
}
